import React, { FunctionComponent, useState } from 'react'

import { FormattedMessage, useIntl } from 'react-intl'
import { useMediaQuery } from 'react-responsive'

import { Card, EligibilityPlanToDisplay, statusResponse } from '@/types'
import { desktopWidth, isP1X } from '@/utils'
import { isDeferred } from '@/utils/regulatoryFigures'
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator'
import Modal from 'components/Modal'
import SkipLinks from 'components/SkipLinks'
import EligibilityPlansButtons from 'Widgets/EligibilityModal/components/EligibilityPlansButtons'
import Schedule from 'Widgets/EligibilityModal/components/Schedule'
import DesktopModal from 'Widgets/EligibilityModal/DesktopModal'
import s from 'Widgets/EligibilityModal/EligibilityModal.module.css'
import MobileModal from 'Widgets/EligibilityModal/MobileModal'

type Props = {
  initialPlanIndex?: number
  onClose: (event: React.MouseEvent | React.KeyboardEvent) => void
  eligibilityPlans: EligibilityPlanToDisplay[]
  status: statusResponse
  cards?: Card[]
}

const EligibilityModal: FunctionComponent<Props> = ({
  initialPlanIndex,
  onClose,
  eligibilityPlans,
  status,
  cards,
}) => {
  const [currentPlanIndex, setCurrentPlanIndex] = useState(initialPlanIndex || 0)
  const isBigScreen = useMediaQuery({ minWidth: desktopWidth })
  const ModalComponent = isBigScreen ? DesktopModal : MobileModal
  const eligiblePlans = eligibilityPlans.filter((plan) => plan.eligible)
  const currentPlan = eligiblePlans[currentPlanIndex]
  const intl = useIntl()

  const isSomePlanDeferred = eligibilityPlans.some(isDeferred)

  // Skip links definition for RGAA accessibility
  // Towards the 3 main sections that users may want to quickly consult
  const skipLinks = [
    {
      href: '#payment-plans',
      labelId: 'skip-links.payment-plans',
      defaultMessage: intl.formatMessage({
        id: 'skip-links.payment-plans',
        defaultMessage: 'Aller aux options de paiement',
      }),
    },
    {
      href: '#payment-info',
      labelId: 'skip-links.payment-info',
      defaultMessage: intl.formatMessage({
        id: 'skip-links.payment-info',
        defaultMessage: 'Aller aux informations de paiement',
      }),
    },
    {
      href: '#payment-schedule',
      labelId: 'skip-links.payment-schedule',
      defaultMessage: intl.formatMessage({
        id: 'skip-links.payment-schedule',
        defaultMessage: 'Aller au calendrier de paiement',
      }),
    },
  ]

  return (
    <Modal
      onClose={onClose}
      ariaHideApp={false}
      scrollable
      id="alma-widget-modal-main-container"
      isOpen
      aria={{
        labelledby: 'modal-title',
        describedby: 'modal-info-element',
      }}
    >
      <SkipLinks skipLinks={skipLinks} />
      <ModalComponent
        isSomePlanDeferred={isSomePlanDeferred}
        cards={cards}
        isCurrentPlanP1X={isP1X(currentPlan)}
      >
        {status === statusResponse.PENDING && (
          <div className={s.loader} role="status" aria-live="polite">
            <LoadingIndicator />
          </div>
        )}
        {status === statusResponse.SUCCESS && eligiblePlans.length === 0 && (
          <div className={s.noEligibility} role="alert">
            <FormattedMessage
              id="eligibility-modal.no-eligibility"
              defaultMessage="Oups, il semblerait que la simulation n'ait pas fonctionné."
            />
          </div>
        )}
        {status === statusResponse.SUCCESS && eligiblePlans.length >= 1 && (
          <>
            <div role="region" aria-labelledby="payment-plans-title">
              <EligibilityPlansButtons
                id="payment-plans"
                eligibilityPlans={eligiblePlans}
                currentPlanIndex={currentPlanIndex}
                setCurrentPlanIndex={setCurrentPlanIndex}
              />
            </div>
            <div className={s.scheduleArea} role="region" aria-labelledby="payment-schedule-title">
              <div className={s.verticalLine} />
              <Schedule id="payment-schedule" currentPlan={currentPlan} />
            </div>
          </>
        )}
      </ModalComponent>
    </Modal>
  )
}
export default EligibilityModal
